// Contracts — лейн app.weern.com в TG-боте (ADR-0082).
//
// Бот дополняет веб/PWA app.weern.com: человек приходит по диплинку, выбирает
// язык, проходит ОДИН пробный кадр и после этого сдаёт фото пачками файлов.
// Здесь живёт всё, что должно судить кадр ОДИНАКОВО на всех поверхностях:
// вокабуляр отказов и чистая функция решения. Пороги не переписываются —
// берутся из eyeq/upload (EYEQ_MIN_PIXELS, EYEQ_MIN_SHORT_EDGE) и eyeq/gates,
// потому что кадр из бота попадает в ТОТ ЖЕ каталог, что кадр из кабинета.
import { z } from 'zod';
import { EYEQ_MIN_PIXELS, EYEQ_MIN_SHORT_EDGE, eyeqZoomNote } from '../eyeq/upload';
import { deviceExifNote, freshnessNote, gpsNote, pixelsNote } from '../eyeq/gates';

/** Языки лейна. Вход всегда на английском, дальше — выбор кнопкой.
 *  Расширять можно, но каждая строка обязана появиться во ВСЕХ языках сразу
 *  (то же правило, что у earner-строк кабинета). */
export const TG_APP_LANGS = ['en', 'es', 'pt'] as const;
export type TgAppLang = (typeof TG_APP_LANGS)[number];
export const TgAppLangSchema = z.enum(TG_APP_LANGS);

/** Состояние человека в лейне. `lang` → выбирает язык, `test` → сдаёт пробный
 *  кадр, `ready` → пачки принимаются. Порядок не перепрыгивается: пока кадр
 *  не прошёл, бот не притворяется, что приём открыт. */
export const TG_APP_STATES = ['lang', 'test', 'ready'] as const;
export type TgAppState = (typeof TG_APP_STATES)[number];

/**
 * ЗАКРЫТЫЙ вокабуляр исходов приёма. Ключ выбирает текст подсказки в трёх
 * языках; неизвестный ключ = человек остался без объяснения, поэтому список
 * закрыт, а тест на полноту переводов пинит его целиком.
 *
 * Часть ключей дословно совпадает с заметками eyeq (`below_min_pixels`,
 * `gps_missing`, `no_capture_date`, `stale_capture`, `no_device_exif`) — это
 * НАМЕРЕННО: одна и та же причина обязана называться одинаково в каталоге и
 * в чате, иначе разбор «почему у человека не берут кадры» становится
 * переводом с одного вокабуляра на другой.
 */
export const TG_APP_NOTES = [
  'too_large',        // > 20 МБ: Bot API таких файлов боту не отдаёт
  'not_an_image',     // прислали не картинку
  'sent_as_photo',    // прислали сжатой картинкой, а не файлом — метаданных уже нет
  'not_a_phone',      // снято камерой/зеркалкой, а не телефоном
  'no_metadata',      // EXIF нет вовсе — файл прошёл через что-то, что его снимает
  'no_device_exif',   // EXIF есть, а модели телефона в нём нет — ОТКАЗ (см. ниже)
  'no_capture_date',  // нет даты съёмки
  'stale_capture',    // кадр старше допустимого
  'gps_missing',      // геопозиция не была включена
  'below_min_pixels', // меньше порога по мегапикселям
  'below_min_edge',   // короткая сторона меньше порога — СОВЕТ, не отказ
  'zoom_digital',     // цифровой зум сверх порога (оптический допустим)
  'dup_precheck',     // дубль серийной съёмки: то же устройство, та же секунда
  'country_off_plan', // по гео кадра — страна, в которой мы не собираем
  'duplicate',        // ровно такие байты у нас уже есть
] as const;
export type TgAppNote = (typeof TG_APP_NOTES)[number];

/**
 * Заметки, которые НЕ отказ.
 *
 * `below_min_edge` в каноне технических гейтов отсутствует: порог там один, по
 * мегапикселям. Но промолчать нельзя — кадр уедет и тихо застрянет на
 * отгрузке, поэтому он остаётся текстом в ответе.
 *
 * `no_device_exif` здесь БЫЛ и убран решением владельца 2026-09-03: «если нет
 * модели телефона, если мы её не видим, то давай мы такое будем отсекать».
 * Лейн намеренно СТРОЖЕ канона (`tech_gates.json` помечает device_exif как
 * advisory), и это не противоречие, а разные точки приложения. Канон описывает
 * приёмку каталога, где кадры с пустой камерой уже лежат и камеру им
 * дозаполняли руками; здесь же кадр сдаёт живой человек, который может
 * переснять СЕЙЧАС. А тот же канон говорит, что с пустыми Make/Model кадр в
 * отгрузку не уходит — значит принять такой кадр молча значит дать человеку
 * работать в мусор.
 */
export const TG_APP_ADVISORIES = ['below_min_edge'] as const;
export type TgAppAdvisory = (typeof TG_APP_ADVISORIES)[number];

const ADVISORY_SET: ReadonlySet<string> = new Set(TG_APP_ADVISORIES);

/** Отказ ли это. Канон severity живёт в одном месте — здесь. */
export function tgAppIsGate(note: TgAppNote): boolean {
  return !ADVISORY_SET.has(note);
}

/** Что бот успел узнать о присланном файле. `exif = null` — EXIF не нашли
 *  вовсе (это отдельная причина, а не «пустые поля»). */
export interface TgAppFrameInput {
  /** Как файл пришёл в Telegram. `photo` = сжатая картинка: EXIF там уже нет
   *  по построению, и это первопричина всего остального. */
  transport: 'document' | 'photo';
  mime: string;
  size_bytes: number;
  width: number | null;
  height: number | null;
  exif: {
    make: string;
    model: string;
    captured_at: string;
    gps_present: boolean;
    /** DigitalZoomRatio (0xA404). Отсутствие тега — НЕ нарушение: большинство
     *  телефонов его просто не пишет. */
    digital_zoom?: number | null;
    /** FocalLengthIn35mmFilm (0xA405) — какой линзой снято. Без него зум
     *  цифровой от оптического не отличить. */
    focal_35mm?: number | null;
  } | null;
  duplicate?: boolean;
  /** Наблюдённый максимум разрешения этой модели, МП (реестр живёт в боте —
   *  это данные, а не правило). null = «про модель не знаем». */
  observed_max_mp?: number | null;
}

export interface TgAppDevice {
  make: string;
  model: string;
  observed_max_mp: number | null;
  /** Мы СВОИМИ ГЛАЗАМИ видели у этой модели кадр нужного размера. Только в
   *  эту сторону реестр и честен: обратного вывода из него нет. */
  proven_capable: boolean;
}

export interface TgAppFrameCheck {
  /** Кадр проходит ГЕЙТЫ. Советы (`advisories`) на это не влияют. */
  ok: boolean;
  /** Провалившиеся ГЕЙТЫ, важное первым. Пусто при ok. */
  notes: TgAppNote[];
  /** Не отказ, но сказать надо: пустая камера, узкая короткая сторона. */
  advisories: TgAppAdvisory[];
  /** С чего начинать разговор. '' при ok. */
  primary: TgAppNote | '';
  /** Что за телефон — нужно подсказке про мегапиксели. null, если EXIF нет. */
  device: TgAppDevice | null;
}

/** Bot API отдаёт файлы до 20 МБ — больше просто не скачать. */
export const TG_APP_MAX_BYTES = 20 * 1024 * 1024;

const IMAGE_MIME = /^image\//;
const IMAGE_EXT = /\.(jpe?g|heic|heif|png|webp|avif|tiff?|dng)$/i;

/** Картинка ли это — по mime ИЛИ по расширению: Telegram нередко отдаёт
 *  HEIC как application/octet-stream. */
export function tgAppIsImage(mime: string, fileName: string): boolean {
  return IMAGE_MIME.test(mime || '') || IMAGE_EXT.test(fileName || '');
}

/** Телефон ли снимал. Порт `mapsPhoneVerdict` НЕ делаем: там свой большой
 *  список под maps-пайплайн. Здесь нужен только грубый признак «это явно
 *  фотоаппарат», а спорные случаи пусть решает QA — ложный отказ живому
 *  человеку дороже пропущенного кадра. */
const CAMERA_MAKES = /^(canon|nikon|fujifilm|olympus|om digital|panasonic|pentax|ricoh|leica|hasselblad|sigma|kodak|gopro|dji|insta360|minolta|casio)/i;
const CAMERA_MODELS = /(ilce|dsc-|dslr|\beos\b|coolpix|lumix|dmc-|\bhero\b|mavic|osmo|theta)/i;

export function tgAppNotAPhone(make: string, model: string): boolean {
  const mk = (make || '').trim();
  const md = (model || '').trim();
  if (!mk && !md) return false; // нет метаданных — это другая причина
  if (CAMERA_MAKES.test(mk)) return true;
  return CAMERA_MODELS.test(`${mk} ${md}`);
}

/**
 * Решение по кадру.
 *
 * Главное правило — ПОДАВЛЕНИЕ ПРОИЗВОДНЫХ. Если человек прислал сжатую
 * картинку, у неё не будет ни даты, ни GPS, ни полного разрешения — и
 * вываливать ему четыре претензии значит утопить единственную настоящую
 * («шли файлом»). Поэтому первопричина отвечает одна, а независимые
 * нарушения перечисляются вместе.
 */
export function tgAppCheckFrame(input: TgAppFrameInput, now: Date = new Date()): TgAppFrameCheck {
  const device: TgAppDevice | null = input.exif
    ? {
      make: input.exif.make,
      model: input.exif.model,
      observed_max_mp: input.observed_max_mp ?? null,
      proven_capable: (input.observed_max_mp ?? 0) * 1e6 >= EYEQ_MIN_PIXELS,
    }
    : null;

  const one = (note: TgAppNote): TgAppFrameCheck =>
    ({ ok: false, notes: [note], advisories: [], primary: note, device });

  // Первопричины: пока они есть, об остальном говорить нечего.
  if (input.size_bytes > TG_APP_MAX_BYTES) return one('too_large');
  if (!tgAppIsImage(input.mime, '')) {
    // mime может быть пустым у document — тогда судим по факту разбора байтов
    // ниже; «не картинка» ставим только когда и размеров не нашлось.
    if (!input.width || !input.height) return one('not_an_image');
  }
  if (input.transport === 'photo') return one('sent_as_photo');
  if (!input.width || !input.height) return one('not_an_image');
  if (!input.exif) return one('no_metadata');
  // Модели телефона нет, хотя EXIF есть. Это ПЕРВОПРИЧИНА того же рода, что
  // «метаданных нет вовсе»: файл почти наверняка пересохранён или прошёл
  // через канал, срезающий метаданные, и остальные замечания были бы
  // производными. Одно понятное указание — «пришли оригинал из штатной
  // камеры» — покрывает их все.
  if (deviceExifNote(input.exif.make, input.exif.model)) return one('no_device_exif');
  if (tgAppNotAPhone(input.exif.make, input.exif.model)) return one('not_a_phone');

  // Дальше — независимые нарушения: их перечисляем вместе, чтобы человек
  // исправил всё за один заход, а не открывал настройки трижды. Порядок —
  // канон `tech_gates.json` (severity gate), советы идут отдельным списком и
  // приёму не мешают.
  const notes: TgAppNote[] = [];
  const advisories: TgAppAdvisory[] = [];

  const fresh = freshnessNote(input.exif.captured_at, now);
  if (fresh === 'no_capture_date') notes.push('no_capture_date');
  else if (fresh === 'stale_capture') notes.push('stale_capture');
  if (gpsNote(input.exif.gps_present)) notes.push('gps_missing');
  if (pixelsNote(input.width, input.height)) notes.push('below_min_pixels');
  // Цифровой зум сверх порога. Судится ВМЕСТЕ с фокусным: Apple пишет в
  // DigitalZoomRatio цифровой множитель, а Samsung/Xiaomi — ПОЛНОЕ увеличение,
  // поэтому кадр с перископа рапортует «5» на чистой оптике. Отсутствие обоих
  // тегов нарушением не является — большинство телефонов их не пишет.
  if (eyeqZoomNote(input.exif.digital_zoom, input.exif.focal_35mm)) notes.push('zoom_digital');

  // Дубль — не претензия к съёмке, а факт про каталог: он не отменяет
  // остальные замечания, но и первым не идёт, если есть что исправить.
  if (input.duplicate) notes.push('duplicate');

  // Совет остался один: короткой стороны в каноне гейтов нет вовсе, но не
  // сказать про неё нельзя — кадр уедет и тихо застрянет на отгрузке.
  if (Math.min(input.width, input.height) < EYEQ_MIN_SHORT_EDGE) advisories.push('below_min_edge');

  if (!notes.length) return { ok: true, notes: [], advisories, primary: '', device };
  return { ok: false, notes, advisories, primary: notes[0], device };
}

/**
 * EXIF-блок для `tgAppCheckFrame`, либо null — «метаданных нет вовсе».
 *
 * `extractExif` при неудаче отдаёт ПУСТУЮ структуру, а не ошибку, поэтому
 * «EXIF не нашли» и «EXIF есть, но пустой» на выходе выглядят одинаково. Для
 * человека это одна и та же причина («снимай основным приложением камеры»), и
 * различать их нам незачем: пустым считаем блок, в котором нет НИ одного из
 * четырёх признаков съёмки.
 */
export function tgAppExifOrNull(exif: {
  make: string;
  model: string;
  captured_at: string;
  gps_present: boolean;
  digital_zoom?: number | null;
  focal_35mm?: number | null;
}): TgAppFrameInput['exif'] {
  const any = exif.make.trim() || exif.model.trim() || exif.captured_at.trim() || exif.gps_present;
  if (!any) return null;
  return {
    make: exif.make,
    model: exif.model,
    captured_at: exif.captured_at,
    gps_present: exif.gps_present,
    digital_zoom: exif.digital_zoom ?? null,
    focal_35mm: exif.focal_35mm ?? null,
  };
}
